import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../../config/apiConfig";

export const useCertificateData = () => {
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isUserOtp, setIsUserOtp] = useState("");
  const [isUserMobile, setIsUserMobile] = useState("");
  const [certificateList, setCertificateList] = useState([]);
  const [showResendOtpTimer, setShowResendOtpTimer] = useState(true);
  const [showNavigationRouter, setShowNavigationRouter] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isCertificateLoading, setIsCertificateLoading] = useState(false);

  // Timer management with ref to avoid stale closure
  const timerRef = useRef();
  useEffect(() => {
    if (timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            setShowResendOtpTimer(false);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  // ****** Send Otp Mobile Api ******
  const sendOtpApi = useCallback((mobile) => {
    if (mobile?.length !== 10) return;
    setLoading(true);
    axios(`${API_URL.GENERATE_OTP_URL}${mobile}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        if (res?.status === 200) {
          setTimeLeft(300);
          setIsOtpSent(true);
          setShowResendOtpTimer(true);
          toast.success("OTP has been sent to your registered mobile number.");
        } else {
          toast.error(res?.data?.message);
        }
      })
      .catch(() => {
        toast.error("Failed to send OTP. Please try again.");
      })
      .finally(() => setLoading(false));
  }, []);

  // ****** Resend Otp Api ******
  const reSendOtpApi = useCallback((mobile) => {
    if (mobile?.length !== 10) return;
    setLoading(true);
    axios(`${API_URL.RESEND_OTP_URL}${mobile}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        if (res?.status === 200) {
          setTimeLeft(300);
          setIsOtpSent(true);
          setShowResendOtpTimer(true);
          toast.success(
            "OTP has been re-sent to your registered mobile number"
          );
        } else {
          toast.error(res?.data?.message);
        }
      })
      .catch(() => {
        toast.error("Failed to resend OTP. Please try again.");
      })
      .finally(() => setLoading(false));
  }, []);

  // ****** Validation ******
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isUserMobile) {
        setErrorMessage("Please enter your mobile number.");
        setShowError(true);
        return;
      }
      if (isUserMobile?.length < 10) {
        setErrorMessage("Mobile number must be 10 digits long.");
        setShowError(true);
        return;
      }
      if (!isUserOtp) {
        setErrorMessage("Please enter your otp.");
        setShowError(true);
        return;
      }
      if (isUserOtp?.length < 6) {
        setErrorMessage("Otp must be 6 digits.");
        setShowError(true);
        return;
      }
      setShowError(false);
      VerifyOtpApi();
    },
    [isUserMobile, isUserOtp]
  );

  const handleClose = () => {
    setShowError(false);
  };

  // ****** Verify Otp Api ******
  const VerifyOtpApi = useCallback(() => {
    setLoading(true);
    axios(`${API_URL.VERIFY_OTP_URL}${isUserMobile}&otp=${isUserOtp}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        if (res?.data?.verified === true) {
          getCertificateListApi(isUserMobile);
          toast.success("OTP successfully verified.");
        } else if (res?.data?.verified === false) {
          toast.error("The OTP you entered is incorrect. Please try again.");
        } else if (res?.status === 404) {
          toast.error(res?.data?.message);
        }
      })
      .catch(() => {
        toast.error("Failed to verify OTP. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [isUserMobile, isUserOtp]);

  // ****** Get Certificate List Api ******
  const getCertificateListApi = useCallback(async (mobile) => {
    setIsCertificateLoading(true);
    axios(API_URL.GET_CERTIFICATE_BY_IMEI_OR_MOBILE("", mobile), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        if (res?.status === 200 && res?.data?.length > 0) {
          setCertificateList(res?.data);
          toast.success(res?.data?.message);
        } else if (res?.status === 200) {
          toast.error(
            "Policy endorsement is pending due to incomplete details or incorrect video."
          );
          setShowNavigationRouter(true);
        } else {
          toast.error(res?.data?.message);
        }
      })
      .catch(() => {
        toast.error("Failed to fetch certificate list. Please try again.");
      })
      .finally(() => setIsCertificateLoading(false));
  }, []);

  // ****** Open Url Api ******
  const onViewCertificate = useCallback((documentUrl) => {
    return documentUrl && window.open(documentUrl, "_blank");
  }, []);

  // Function to Reset Inputs and Search Data
  const handleRefresh = () => {
    setIsUserMobile("");
    setIsUserOtp("");
    setCertificateList([]);
    setIsOtpSent(false);
  };

  return {
    handleSubmit,
    isUserMobile,
    setIsUserMobile,
    sendOtpApi,
    isUserOtp,
    setIsUserOtp,
    showResendOtpTimer,
    setShowResendOtpTimer,
    formatTime,
    reSendOtpApi,
    showNavigationRouter,
    certificateList,
    onViewCertificate,
    handleRefresh,
    showError,
    handleClose,
    errorMessage,
    loading,
    timeLeft,
    setTimeLeft,
    isOtpSent,
    isCertificateLoading,
  };
};
